import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';

/**
 * Component to display a build phase header with progress information
 * @param {Object} props - Component props 
 * @param {string} props.phaseName - The name of the build phase
 * @param {number} props.taskCount - Total number of tasks in this phase
 * @param {number} props.completedCount - Number of completed tasks in this phase
 * @returns {React.ReactElement} Phase header component
 */
const PhaseHeader = ({ phaseName, taskCount, completedCount }) => {
  // Calculate progress percentage
  const progressPercentage = taskCount > 0 
    ? Math.round((completedCount / taskCount) * 100) 
    : 0;
  
  // Generate description based on phase name
  const getPhaseDescription = (phase) => {
    const phaseDescriptions = {
      'Setup & Infrastructure': 'Prepare the development environment and infrastructure components',
      'Core Components': 'Implement foundational UI and data components',
      'Feature Implementation': 'Develop user-facing features and functionality',
      'Testing & QA': 'Perform testing, bug fixing, and quality assurance',
      'Deployment & Release': 'Prepare for production deployment and release',
      'Documentation': 'Create user guides and developer documentation',
      // Add more descriptions as needed
    };
    
    return phaseDescriptions[phase] || '';
  };

  return (
    <Box 
      sx={{ 
        width: { xs: '100%', md: '280px' },
        minWidth: { md: '250px' },
        pt: 2,
        pr: { xs: 0, md: 3 },
        position: 'relative'
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.25rem', md: '1.4rem' },
            mb: 1,
            color: theme => theme.palette.text.primary
          }}
        >
          {phaseName}
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {getPhaseDescription(phaseName)}
        </Typography>
        
        {/* Progress tracker */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mt: 2,
            mb: 1
          }}
        >
          <Tooltip title={`${completedCount} of ${taskCount} tasks completed`}>
            <Box sx={{ flex: 1, mr: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progressPercentage === 100 
                      ? '#4caf50' // Green when complete
                      : progressPercentage > 50 
                        ? '#2196f3' // Blue when over halfway
                        : progressPercentage > 0 
                          ? '#ff9800' // Orange when started
                          : '#9e9e9e', // Gray when not started
                  }
                }}
              />
            </Box>
          </Tooltip>
          
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              minWidth: '45px',
              textAlign: 'right',
              fontWeight: 500
            }}
          >
            {progressPercentage}%
          </Typography>
        </Box>
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 400,
            display: 'block'
          }}
        >
          {completedCount} of {taskCount} tasks completed
        </Typography>
      </Box>
    </Box>
  );
};

PhaseHeader.propTypes = {
  phaseName: PropTypes.string.isRequired,
  taskCount: PropTypes.number.isRequired,
  completedCount: PropTypes.number.isRequired
};

export default React.memo(PhaseHeader);